import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Chip,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  DirectionsCar as TruckIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  QrCode as QrCodeIcon,
} from "@mui/icons-material";
import { useAppSelector } from "../../app/hooks";
import { useGetVehicleAssignmentsOfDriverQuery } from "../../api/truckAccountsApi";
import QRCodeModal from "../../components/common/QRCodeModal/QRCodeModal";

const TruckDriverPage: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);

  const {
    data: vehicleAssignments,
    isLoading: isLoadingAssignments,
    error: assignmentsError
  } = useGetVehicleAssignmentsOfDriverQuery();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <TruckIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Truck Driver Portal
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Welcome back, {user?.firstName || "Driver"}!
        </Typography>
        <Chip
          label="Truck Driver"
          color="primary"
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Vehicle Assignments Info and QR Code */}
      {isLoadingAssignments ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <CircularProgress />
        </Box>
      ) : assignmentsError ? (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            ⚠️ Vehicle Assignment Required
          </Typography>
          <Typography variant="body1">
            You are not currently assigned to any vehicles. Please contact your administrator to assign you to a vehicle.
          </Typography>
        </Alert>
      ) : vehicleAssignments && vehicleAssignments.length > 0 ? (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            🚛 Your Assigned Vehicles
          </Typography>

          {/* Vehicle Selection and QR Code */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Vehicle for QR Code</InputLabel>
              <Select
                value={selectedVehicleId || ''}
                onChange={(e) => setSelectedVehicleId(e.target.value as number)}
                label="Select Vehicle for QR Code"
              >
                {vehicleAssignments.map((assignment) => (
                  <MenuItem key={assignment.id} value={assignment.vehicleId}>
                    {assignment.vehicleName || `Vehicle ${assignment.vehicleId}`}
                    {assignment.vehicleVIN && ` (VIN: ${assignment.vehicleVIN})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<QrCodeIcon />}
              onClick={() => setQrModalOpen(true)}
              disabled={!selectedVehicleId}
              sx={{ textTransform: 'none' }}
            >
              Generate Documents QR
            </Button>
          </Box>

          {/* Vehicle Cards */}
          <Grid container spacing={2}>
            {vehicleAssignments.map((assignment) => (
              <Grid item xs={12} sm={6} md={4} key={assignment.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {assignment.vehicleName || `Vehicle ${assignment.vehicleId}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Vehicle ID: {assignment.vehicleId}
                    </Typography>
                    {assignment.vehicleVIN && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        VIN: {assignment.vehicleVIN}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Assigned: {new Date(assignment.startDate).toLocaleDateString()}
                    </Typography>
                    {assignment.endDate && (
                      <Typography variant="body2" color="error">
                        Ended: {new Date(assignment.endDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : null}

      {/* Coming Soon Alert */}
      <Alert
        severity="info"
        sx={{ mb: 4 }}
        icon={<ScheduleIcon />}
      >
        <Typography variant="h6" gutterBottom>
          🚧 Coming Soon
        </Typography>
        <Typography variant="body1">
          The truck driver portal is currently under development. Soon you'll be able to:
        </Typography>
      </Alert>

      {/* Feature Preview Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center", height: "100%" }}>
            <LocationIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Route Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage your assigned routes, delivery schedules, and navigation.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center", height: "100%" }}>
            <TruckIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Vehicle Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor your vehicle's status, maintenance alerts, and fuel levels.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center", height: "100%" }}>
            <ScheduleIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Hours of Service
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track your driving hours, rest periods, and compliance requirements.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Status Information */}
      <Box sx={{ mt: 4, p: 3, bgcolor: "grey.50", borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Current Status
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You are successfully logged in as a truck driver. The system is ready to provide you with
          real-time information and tools to help you manage your deliveries efficiently.
        </Typography>
      </Box>

      {/* QR Code Modal */}
      {selectedVehicleId && vehicleAssignments && (
        <QRCodeModal
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          title="Vehicle Permit Documents"
          url={`${window.location.origin}/permits/${selectedVehicleId}`}
          description={`Scan this QR code to view permit documents for ${vehicleAssignments.find(a => a.vehicleId === selectedVehicleId)?.vehicleName || `Vehicle ${selectedVehicleId}`}`}
        />
      )}
    </Container>
  );
};

export default TruckDriverPage;
